import React from 'react';
import ArticleCover from './ArticleCover';
import ArticleImage from './ArticleImage';
import { renderInline } from './RichText';
import { getBadgeColor, formatDate, formatReadTime } from '../pages/articles.utils';
import '../pages/Article.css';

/**
 * Rendu d'un article — purement présentationnel.
 *
 * Extrait de la page `/article/:id` pour que l'éditeur puisse afficher un aperçu
 * du **même** rendu que le lecteur. Sans cette séparation, l'aperçu serait une
 * seconde implémentation du gabarit, qui divergerait dès la première retouche.
 *
 * Ne contient volontairement pas de `PageHeader` : son bouton avatar navigue vers
 * `/profil`, ce qui n'a pas de sens dans un aperçu. La page le pose elle-même.
 *
 * `article` suit la forme renvoyée par l'API ; en aperçu, l'éditeur passe le
 * payload qu'il s'apprête à enregistrer, ce qui garantit que ce qu'on voit est
 * bien ce qui sera publié.
 */
// Un bloc, un rendu. L'ordre de `article.blocs` porte à lui seul la mise en page :
// aucun regroupement n'est reconstruit ici, un intertitre est un bloc parmi les
// autres. Les textes passent par `renderInline` pour le gras et les liens.
const renderBloc = (bloc, index) => {
  switch (bloc.type) {
    case 'titre':
      return (
        <h2 key={index} className="article-page__section-title article-page__block--titre">
          {bloc.texte}
        </h2>
      );

    case 'paragraphe':
      return (
        <p key={index} className="article-page__section-text">
          {renderInline(bloc.texte)}
        </p>
      );

    case 'image':
      return <ArticleImage key={index} url={bloc.url} legende={bloc.legende} />;

    case 'puces':
      return (
        <ul key={index} className="article-page__bullets">
          {(bloc.items || []).map((item, ii) => (
            <li key={ii} className="article-page__bullet">{renderInline(item)}</li>
          ))}
        </ul>
      );

    case 'liste':
      return (
        <ol key={index} className="article-page__list">
          {(bloc.items || []).map((item, ii) => (
            <li key={ii} className="article-page__list-item">
              <span className="article-page__list-num">{ii + 1}.</span>
              <div className="article-page__list-body">
                <strong className="article-page__list-subtitle">{item.sous_titre}</strong>
                <p className="article-page__list-text">{renderInline(item.texte)}</p>
              </div>
            </li>
          ))}
        </ol>
      );

    default:
      // Un type inconnu vient forcément d'une version plus récente de l'éditeur :
      // on saute le bloc plutôt que de casser la page.
      return null;
  }
};

const ArticleView = ({ article, onSelectSimilar }) => {
  const similar = article.similar || [];
  const mainCategory = (article.categories || [])[0];

  return (
    <>
      {/* Hero image */}
      <ArticleCover
        className="article-page__hero"
        id={article.id}
        imageUrl={article.image_url}
        titre={article.titre}
        category={mainCategory}
        glyphSize="lg"
        eager
      />

      {/* Category badges */}
      <div className="article-page__badges">
        {(article.categories || []).map((cat) => (
          <span
            key={cat}
            className="article-page__badge"
            style={{ background: getBadgeColor(cat) }}
          >
            {cat}
          </span>
        ))}
      </div>

      {/* Title */}
      <h1 className="article-page__title">{article.titre}</h1>

      {/* Meta */}
      <div className="article-page__meta">
        {article.auteur && <span>✍️ Par {article.auteur}</span>}
        <span>📅 {formatDate(article.date_publication)}</span>
        <span>⏱ {formatReadTime(article.temps_lecture, true)}</span>
      </div>

      {/* Widget Article */}
      <div className="article-page__widget">
        <div className="article-page__content">
          {/* Intro */}
          <p className="article-page__intro">{article.intro}</p>

          {/* Callout */}
          {article.callout && (
            <div className="article-page__callout">
              <p>{article.callout}</p>
            </div>
          )}

          {/* Corps : la suite de blocs, rendue dans l'ordre */}
          {(article.blocs || []).map(renderBloc)}
        </div>
      </div>

      {/* Articles similaires — absents en aperçu, le bloc se masque de lui-même */}
      {similar.length > 0 && (
        <div className="article-page__similar">
          <h2 className="article-page__similar-title">Articles similaires</h2>
          <div className="article-page__similar-grid">
            {similar.map((a) => {
              const category = (a.categories || [])[0];
              return (
                <div
                  key={a.id}
                  className="article-page__similar-card"
                  onClick={() => onSelectSimilar?.(a.id)}
                >
                  <ArticleCover
                    className="article-page__similar-img"
                    id={a.id}
                    imageUrl={a.image_url}
                    titre={a.titre}
                    category={category}
                    glyphSize="sm"
                  >
                    {category && (
                      <span
                        className="article-page__similar-badge"
                        style={{ background: getBadgeColor(category) }}
                      >
                        {category}
                      </span>
                    )}
                  </ArticleCover>
                  <p className="article-page__similar-name">{a.titre}</p>
                  <span className="article-page__similar-time">⏱ {formatReadTime(a.temps_lecture)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </>
  );
};

export default ArticleView;
